import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useAuth0 } from '@auth0/auth0-react';
import {
  AgentEvents,
  GetCompleteProDocResponse,
  GetProSheetResponse,
  ProDocUpdatedEvent,
  ProDocRenamedEvent,
  ProSheetData,
  ProSheetUpdatedEvent,
} from '@dreamai/types';
import { AppTab, AppTabAction } from './appLayoutConfig';
import api from '../../../api';
import { usePusher } from '../../../contexts/PusherContext';
import ProDocDisplay from '../ProDocDisplay';
import { ProDocSheetDisplay } from '../ProDocSheetDisplay';
import { ReactComponent as DownloadIcon } from '../../../icons/download.svg';
import { ReactComponent as FullscreenIcon } from '../../../icons/fullscreen.svg';
import AgentStatusPill from './AgentStatusPill';
import SidewaysLoading from '../sidebar/SidewaysLoading';
import ErrorState from '../../common/ErrorState';
import './company-research-wrapper.scss';
import '../docs-dashboard.scss';

interface CompanyResearchWrapperProps {
  proDocId: string;
  mode: 'single' | 'multiple';
  threadId?: string;
  tabId?: string;
  updateThreadViewVisualDescription?: (
    visualDescription: string,
  ) => Promise<void>;
  onTabsReady?: (tabs: AppTab[]) => void;
  onActionsReady?: (actions: AppTabAction[]) => void;
}

const CompanyResearchWrapper: React.FC<CompanyResearchWrapperProps> = ({
  proDocId,
  mode,
  threadId,
  tabId,
  updateThreadViewVisualDescription,
  onTabsReady,
  onActionsReady,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const pusherChannel = usePusher();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAccessDenied, setIsAccessDenied] = useState(false);
  const [proDocTitle, setProDocTitle] = useState<string>('');
  const [proDocData, setProDocData] = useState<any>(null);
  const [sheetData, setSheetData] = useState<any>(null);
  const lastTabsRef = useRef<string>('');
  const lastLocalUpdateRef = useRef<number>(0);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setIsAccessDenied(false);
      const token = await getAccessTokenSilently();

      if (mode === 'multiple') {
        // Fetch sheet data
        const response = await api.get<GetProSheetResponse>(
          `/pro-docs/${proDocId}/sheet`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        if (response.data.success && response.data.proDoc) {
          setProDocTitle(response.data.proDoc.title);
          setSheetData(response.data.data);
        } else {
          setError('Failed to load sheet data');
        }
      } else {
        // Fetch doc data
        const response = await api.get<GetCompleteProDocResponse>(
          `/pro-docs/${proDocId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        if (response.data.success && response.data.proDoc) {
          setProDocTitle(response.data.proDoc.title);
          setProDocData(response.data);
        } else {
          setError('Failed to load document data');
        }
      }
    } catch (err: any) {
      console.error('Error fetching company research data:', err);
      if (err?.response?.status === 403) {
        setIsAccessDenied(true);
        setError('Access denied');
      } else {
        setError('Failed to load company research data');
      }
    } finally {
      setLoading(false);
    }
  }, [proDocId, mode, getAccessTokenSilently]);

  // Fetch data on mount and when proDocId changes
  useEffect(() => {
    setLoading(true);
    lastTabsRef.current = '';
    fetchData();
  }, [proDocId, mode, fetchData]);

  // Notify parent of tabs when data is loaded
  useEffect(() => {
    if (loading || !onTabsReady || !proDocTitle) return;

    const tabs: AppTab[] = [
      {
        id: 'research',
        label: proDocTitle,
      },
    ];

    const tabsKey = JSON.stringify(tabs);
    if (tabsKey !== lastTabsRef.current) {
      lastTabsRef.current = tabsKey;
      onTabsReady(tabs);
    }
  }, [loading, proDocTitle, onTabsReady]);

  // Update visual description
  useEffect(() => {
    if (proDocTitle && updateThreadViewVisualDescription) {
      updateThreadViewVisualDescription(proDocTitle);
    }
  }, [proDocTitle, updateThreadViewVisualDescription]);

  // Fetch only sheet data (for Pusher updates)
  const fetchSheetDataOnly = useCallback(async () => {
    const timeSinceLocalUpdate = Date.now() - lastLocalUpdateRef.current;
    if (timeSinceLocalUpdate < 2000) return;

    try {
      const token = await getAccessTokenSilently();
      const response = await api.get<GetProSheetResponse>(
        `/pro-docs/${proDocId}/sheet`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (response.data.success && response.data.data) {
        setSheetData(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching sheet data:', err);
    }
  }, [proDocId, getAccessTokenSilently]);

  // Listen for ProDoc/ProSheet updates via Pusher
  // Note: Sheet manipulation tools (add/remove columns) trigger PRO_DOC_UPDATED
  // while cell updates trigger PRO_SHEET_UPDATED
  useEffect(() => {
    if (!pusherChannel || !proDocId) return;

    const handleProDocUpdate = (data: ProDocUpdatedEvent) => {
      if (data.proDocId === proDocId) {
        if (mode === 'single') {
          fetchData();
        } else {
          // Sheet structure changed (columns/rows added/removed)
          fetchSheetDataOnly();
        }
      }
    };

    const handleProSheetUpdate = (data: ProSheetUpdatedEvent) => {
      if (data.proDocId === proDocId && mode === 'multiple') {
        // Cell content updated
        fetchSheetDataOnly();
      }
    };

    const handleProDocRenamed = (data: ProDocRenamedEvent) => {
      if (data.proDocId === proDocId) {
        setProDocTitle(data.newTitle);
      }
    };

    pusherChannel.bind(AgentEvents.PRO_DOC_UPDATED, handleProDocUpdate);
    pusherChannel.bind(AgentEvents.PRO_SHEET_UPDATED, handleProSheetUpdate);
    pusherChannel.bind(AgentEvents.PRO_DOC_RENAMED, handleProDocRenamed);

    return () => {
      pusherChannel.unbind(AgentEvents.PRO_DOC_UPDATED, handleProDocUpdate);
      pusherChannel.unbind(AgentEvents.PRO_SHEET_UPDATED, handleProSheetUpdate);
      pusherChannel.unbind(AgentEvents.PRO_DOC_RENAMED, handleProDocRenamed);
    };
  }, [pusherChannel, proDocId, mode, fetchData, fetchSheetDataOnly]);

  // Handle download
  const handleDownload = useCallback(async () => {
    setIsDownloading(true);
    try {
      const token = await getAccessTokenSilently();
      const response = await api.get(`/pro-doc-downloads/${proDocId}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const extension = mode === 'multiple' ? 'csv' : 'pdf';
      const filename = `${proDocTitle || 'download'}.${extension}`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading:', error);
    } finally {
      setIsDownloading(false);
    }
  }, [proDocId, proDocTitle, mode, getAccessTokenSilently]);

  // Notify parent of actions when data is loaded (only for sheets)
  useEffect(() => {
    if (loading || !onActionsReady) return;

    if (mode === 'multiple' && sheetData) {
      const actions: AppTabAction[] = [
        {
          id: 'fullscreen',
          icon: <FullscreenIcon />,
          onClick: () => setIsFullscreen(true),
          title: 'Fullscreen',
        },
        {
          id: 'download',
          icon: isDownloading ? <SidewaysLoading /> : <DownloadIcon />,
          onClick: handleDownload,
          title: 'Download',
          disabled: isDownloading,
        },
      ];
      onActionsReady(actions);
    } else {
      onActionsReady([]);
    }
  }, [loading, mode, sheetData, isDownloading, onActionsReady, handleDownload]);

  // Handle sheet data changes
  const handleSheetDataChange = useCallback(
    async (newData: ProSheetData) => {
      try {
        lastLocalUpdateRef.current = Date.now();
        const token = await getAccessTokenSilently();
        await api.patch(
          `/pro-docs/${proDocId}/sheet`,
          { data: newData },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setSheetData(newData);
      } catch (err) {
        console.error('Error updating sheet:', err);
      }
    },
    [proDocId, getAccessTokenSilently],
  );

  if (loading) {
    return (
      <div className='company-research-wrapper loading'>
        <div className='loading-spinner' />
        <p>Loading research data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className={`company-research-wrapper ${isAccessDenied ? 'access-denied' : 'error'}`}
      >
        {isAccessDenied ? (
          <ErrorState
            title="You don't have access to this document"
            subtitle='Ask the thread owner or document creator to share it with you'
          />
        ) : (
          <ErrorState
            title="I can't load this document"
            subtitle='Please refresh the page and try again'
          />
        )}
      </div>
    );
  }

  const sheetContent = mode === 'multiple' && sheetData && (
    <ProDocSheetDisplay
      proDocId={proDocId}
      initialData={sheetData}
      readOnly={false}
      onDataChange={handleSheetDataChange}
    />
  );

  return (
    <>
      <div
        className={`company-research-wrapper ${mode === 'multiple' ? 'sheet-mode' : ''}`}
      >
        <div className='content-inner'>
          {mode === 'multiple' && sheetData ? (
            <div className='sheet-wrapper'>
              <div className='sheet-container'>{sheetContent}</div>
            </div>
          ) : proDocData ? (
            <div className='pro-doc-view'>
              <ProDocDisplay proDocId={proDocId} />
            </div>
          ) : (
            <div className='no-data'>
              <p>No research data available yet.</p>
            </div>
          )}
        </div>
      </div>

      {isFullscreen &&
        ReactDOM.createPortal(
          <div className='sheet-fullscreen-modal'>
            <div className='sheet-fullscreen-header'>
              <h2>{proDocTitle}</h2>
              <div className='sheet-fullscreen-actions'>
                <button
                  className='action-button'
                  onClick={handleDownload}
                  disabled={isDownloading}
                  title='Download'
                >
                  {isDownloading ? <SidewaysLoading /> : <DownloadIcon />}
                </button>
                <button
                  className='action-button close-button'
                  onClick={() => setIsFullscreen(false)}
                  title='Close'
                >
                  ✕
                </button>
              </div>
            </div>
            <div className='sheet-fullscreen-content'>
              <ProDocSheetDisplay
                proDocId={proDocId}
                initialData={sheetData}
                readOnly={false}
                onDataChange={handleSheetDataChange}
              />
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};

export default CompanyResearchWrapper;
